import React, { useCallback, useEffect, useState } from "react";
import { X, MessageCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import ChatSheet from "@/components/price/ChatSheet";

/** Liste des conversations pré-réservation (trajets publiés) pour un rôle. */
export function useTripThreads(role, pollMs = 15000) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`/price/trip-threads/mine?role=${role}`);
      setItems(r.data?.items || []);
      setUnread(r.data?.unread_total || 0);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [role]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);
  return { items, unread, loading, reload: load };
}

export function TripChat({ thread, role, onClose }) {
  const other = role === "driver" ? thread?.client_name : thread?.driver_name;
  return (
    <ChatSheet
      open={!!thread}
      onClose={onClose}
      requestId={thread?.id}
      selfRole={role}
      counterpartName={other ? `${other} · ${thread?.date || ""} ${thread?.time || ""}` : ""}
      apiBaseOverride={thread ? `/price/trip-threads/${thread.id}/messages` : undefined}
      subtitle="Discussion avant réservation"
      quickRepliesOverride={role === "driver"
        ? ["Oui, il reste de la place", "Bagage OK", "Désolé, c'est complet", "Réserve directement dans l'app"]
        : ["Il reste de la place ?", "Bagage possible ?", "Point de rendez-vous ?", "Merci !"]}
    />
  );
}

export default function TripThreadsSheet({ open, onClose, role, threads }) {
  const [active, setActive] = useState(null);
  const { items, loading, reload } = threads;
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" data-testid={`trip-threads-sheet-${role}`}>
      <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white p-5 street-border" style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}>
        <div className="flex items-center justify-between">
          <h2 className="font-[Outfit] text-[18px] font-black">
            {role === "driver" ? "Messages passagers" : "Mes messages conducteurs"}
          </h2>
          <button onClick={onClose} className="street-press grid h-9 w-9 place-items-center rounded-xl bg-white street-border-thin" data-testid="trip-threads-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        {loading && items.length === 0 && <Loader2 className="mx-auto mt-6 h-5 w-5 animate-spin text-primary" />}
        {!loading && items.length === 0 && (
          <div className="mt-6 rounded-2xl bg-secondary/40 p-5 text-center text-[13px] font-medium text-foreground/60 street-border-thin" data-testid="trip-threads-empty">
            <MessageCircle className="mx-auto mb-2 h-7 w-7 text-foreground/30" />
            Aucune conversation pour l&apos;instant.
          </div>
        )}
        <div className="mt-4 space-y-2">
          {items.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t)}
              className="street-press w-full rounded-2xl bg-white p-3 text-left street-border-thin street-shadow-sm"
              data-testid={`trip-thread-${t.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-[Outfit] text-[14px] font-black truncate">
                  {role === "driver" ? t.client_name : t.driver_name}
                </div>
                {t.unread > 0 && (
                  <span className="grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-black text-primary-foreground" data-testid={`trip-thread-unread-${t.id}`}>
                    {t.unread}
                  </span>
                )}
              </div>
              <div className="text-[11px] font-bold text-foreground/60 truncate">
                {t.date} · {t.time} — {t.from_address} → {t.to_address}
              </div>
              <div className="mt-1 text-[12px] text-foreground/80 truncate">{t.last_text}</div>
            </button>
          ))}
        </div>
      </div>
      <TripChat thread={active} role={role} onClose={() => { setActive(null); reload(); }} />
    </div>
  );
}
